import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BadgePercent, Headset, Sparkles } from "lucide-react";
import { getVehicles, getPosts } from "../lib/data";
import { ADVANTAGES, HERO_IMAGE, CORPORATE_IMAGE } from "../lib/content";
import { useSettings } from "../lib/settings";
import { Reveal } from "../components/Reveal";
import { QuoteForm } from "../components/QuoteForm";
import { usePageMeta } from "@/hooks/use-page-meta";
import { formatPrice, formatMonthlyKm } from "@/lib/price";

const ADV_ICONS = { bakim: ShieldCheck, vergi: BadgePercent, destek: Headset, filo: Sparkles };

function VehicleCard({ v, i }) {
  return (
    <Reveal delay={i * 0.12}>
      <Link
        to={`/filo/${v.slug}`}
        data-testid={`vehicle-card-${v.slug}`}
        className="group block bg-[#FAFBF6] border border-[#B09060]/20 shadow-[0_4px_20px_rgba(176,144,96,0.05)] hover:border-[#B09060]/50 transition-colors duration-300"
      >
        <div className="aspect-[4/3] overflow-hidden relative">
          <img
            src={v.image_url}
            alt={v.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            loading="lazy"
          />
          {v.status && v.status !== "müsait" && (
            <span className="absolute top-3 right-3 px-3 py-1 rounded-sm bg-white/90 backdrop-blur text-[#8F7648] text-[10px] font-semibold uppercase tracking-widest border border-[#B09060]/30" data-testid={`vehicle-status-badge-${v.slug}`}>
              {v.status}
            </span>
          )}
        </div>
        <div className="p-7">
          {v.badge && (
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#B09060]">
              {v.badge}
            </div>
          )}
          <h3 className="font-serif-display text-xl md:text-2xl font-medium text-[#404040] mt-2">
            {v.name}
          </h3>
          <div className="text-xs text-[#6B6B6B] tracking-wide mt-1">{v.segment}</div>
          <div className="flex items-end justify-between mt-5 pt-5 border-t border-[#B09060]/15">
            <div>
              <span className="text-lg font-semibold text-[#404040]">{formatPrice(v.price_label)}</span>
              <span className="text-xs text-[#6B6B6B] ml-1.5">+ KDV / Ay</span>
              {formatMonthlyKm(v.monthly_km) && (
                <div className="text-xs text-[#6B6B6B] mt-1">{formatMonthlyKm(v.monthly_km)}</div>
              )}
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#8F7648] group-hover:gap-3 transition-all duration-300">
              İncele <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
}

export default function Home() {
  usePageMeta({});
  const settings = useSettings();
  const [vehicles, setVehicles] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    getVehicles().then(setVehicles);
    getPosts().then((p) => setPosts(p.slice(0, 3)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-end overflow-hidden" data-testid="hero-section">
        <img
          src={HERO_IMAGE}
          alt="VIA PRIME lüks kurumsal sedan"
          className="absolute inset-0 w-full h-full object-cover d3-drift"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FAFBF6] via-[#FAFBF6]/85 via-55% to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pb-20 pt-44 w-full">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8F7648] mb-4">
            {settings.hero_label}
          </div>
          <h1
            className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium text-[#404040] leading-[1.08] max-w-2xl"
            data-testid="hero-headline"
          >
            <span className="ln"><span>Her Yolculuk</span></span>
            <span className="ln"><span className="italic text-[#B09060]">Bir Prestij</span></span>
          </h1>
          <p className="text-base leading-relaxed text-[#6B6B6B] max-w-xl mt-5" data-testid="hero-subheadline">
            {settings.hero_subline}
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <a
              href="#quote"
              data-testid="hero-quote-cta"
              className="px-8 py-4 bg-[#B09060] text-[#FAFBF6] font-semibold text-xs uppercase tracking-[0.15em] rounded-sm hover:bg-[#8F7648] hover:-translate-y-px transition-all duration-300"
            >
              Kurumsal Teklif İste
            </a>
            <Link
              to="/filo"
              data-testid="hero-fleet-cta"
              className="px-8 py-4 border border-[#404040]/30 text-[#404040] font-semibold text-xs uppercase tracking-[0.15em] rounded-sm hover:border-[#B09060] hover:text-[#8F7648] transition-colors duration-300"
            >
              Filoyu İncele
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURES BAR */}
      <section className="relative z-10 -mt-4 pb-24 md:pb-32 px-6" data-testid="advantages-section">
        <div className="max-w-7xl mx-auto lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {ADVANTAGES.map((a, i) => {
              const Icon = ADV_ICONS[a.key] || Sparkles;
              return (
                <Reveal key={a.key} delay={i * 0.1}>
                  <div
                    className="bg-[#FAFBF6] border border-[#B09060]/20 p-7 h-full shadow-[0_4px_20px_rgba(176,144,96,0.05)]"
                    data-testid={`advantage-card-${a.key}`}
                  >
                    <Icon className="w-7 h-7 text-[#B09060]" strokeWidth={1.25} />
                    <h3 className="text-base font-semibold text-[#404040] mt-4">{a.title}</h3>
                    <p className="text-sm text-[#6B6B6B] leading-relaxed mt-2">{a.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FLEET */}
      <section id="fleet" className="pb-24 md:pb-32 px-6" data-testid="fleet-section">
        <div className="max-w-7xl mx-auto lg:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-14">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8F7648]">
                  2026 Model · Sıfır Kilometre
                </span>
                <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight font-normal text-[#404040] mt-3">
                  Ayrıcalıklı Filomuz
                </h2>
              </div>
              <Link
                to="/filo"
                data-testid="fleet-view-all-link"
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#8F7648] hover:text-[#B09060] transition-colors"
              >
                Tüm Filoyu Gör <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {vehicles.map((v, i) => (
              <VehicleCard key={v.id} v={v} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CORPORATE BANNER (dramatic dark break) */}
      <section className="bg-[#1C1C1C] py-24 md:py-32 px-6" data-testid="corporate-section">
        <div className="max-w-7xl mx-auto lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="overflow-hidden">
              <img
                src={CORPORATE_IMAGE}
                alt="Kurumsal filo çözümleri"
                className="w-full h-[420px] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                loading="lazy"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div>
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#B09060]">
                Kurumsal Çözümler
              </span>
              <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight font-normal text-[#FAFBF6] mt-3 leading-snug">
                Şirketinizin filo ihtiyaçlarına özel çözümler
              </h2>
              <p className="text-base text-[#FAFBF6]/60 leading-relaxed mt-5 max-w-md">
                Operasyonel kiralama modeliyle araç yatırımınızı öngörülebilir
                aylık giderlere dönüştürün; bakım, sigorta ve lastik
                süreçlerinin tamamını biz yönetelim.
              </p>
              <ul className="mt-7 space-y-3 text-sm text-[#FAFBF6]/70">
                {["Operasyonel Kiralama", "Vergisel Avantaj", "Nakit Akışı Koruması", "VIP Şoförlü Hizmet"].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <span className="w-1 h-1 bg-[#B09060] rounded-full shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/kurumsal"
                data-testid="corporate-band-cta"
                className="inline-block mt-9 px-8 py-4 bg-[#B09060] text-[#FAFBF6] font-semibold text-xs uppercase tracking-[0.15em] rounded-sm hover:bg-[#C8A878] hover:-translate-y-px transition-all duration-300"
              >
                Kurumsal Teklif İste
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST — honest placeholder */}
      <section className="py-20 px-6" data-testid="trust-section">
        <div className="max-w-7xl mx-auto lg:px-8">
          <Reveal>
            <div className="border border-[#B09060]/20 bg-[#F2F3EB] p-10 text-center">
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8F7648]">
                Güvenilirlik
              </span>
              <h3 className="font-serif-display text-2xl md:text-3xl font-normal text-[#404040] mt-3">
                Müşteri Deneyimleri
              </h3>
              <p className="text-sm text-[#6B6B6B] mt-3" data-testid="trust-placeholder-text">
                Kurumsal müşterilerimizin ilk referans ve yorumları burada listelenecektir.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BLOG */}
      <section className="pb-24 md:pb-32 px-6" data-testid="blog-section">
        <div className="max-w-7xl mx-auto lg:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-14">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8F7648]">
                  Editöryal
                </span>
                <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight font-normal text-[#404040] mt-3">
                  Güncel Yazılar
                </h2>
              </div>
              <Link
                to="/blog"
                data-testid="blog-view-all-link"
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#8F7648] hover:text-[#B09060] transition-colors"
              >
                Tüm Yazılar <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group block bg-[#FAFBF6] border border-[#B09060]/20 shadow-[0_4px_20px_rgba(176,144,96,0.05)] hover:border-[#B09060]/50 transition-colors duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={p.image_url}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif-display text-xl font-medium text-[#404040] leading-snug group-hover:text-[#8F7648] transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-sm text-[#6B6B6B] leading-relaxed mt-3 line-clamp-2">
                      {p.excerpt}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section id="quote" className="bg-[#F2F3EB] py-24 md:py-32 px-6" data-testid="quote-section">
        <div className="max-w-2xl mx-auto">
          <Reveal>
            <div className="text-center">
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8F7648]">
                Hemen Başlayın
              </span>
              <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight font-normal text-[#404040] mt-3">
                Hızlı Teklif Formu
              </h2>
              <p className="text-sm text-[#6B6B6B] leading-relaxed mt-4">
                Formu doldurun, kurumsal ekibimiz en kısa sürede size özel teklifle dönsün.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10">
              <QuoteForm formType="teklif" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
